import { useLocation, useNavigate, useParams, Navigate } from 'react-router-dom';

/**
 * Utilidades para manejo de rutas (versión modernizada)
 */

/**
 * Guarda la última ubicación en sessionStorage
 */
export const saveLastLocation = (location) => {
  if (location && location.pathname) {
    try {
      sessionStorage.setItem('lastLocation', JSON.stringify({
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
        state: location.state
      }));
    } catch (error) {
      console.warn('Error saving last location:', error);
    }
  }
};

/**
 * Obtiene la última ubicación guardada
 */
export const getLastLocation = () => {
  try {
    const saved = sessionStorage.getItem('lastLocation');
    return saved ? JSON.parse(saved) : { pathname: '/home' };
  } catch (error) {
    console.warn('Error getting last location:', error);
    return { pathname: '/home' };
  }
};

/**
 * Limpia la última ubicación guardada
 */
export const clearLastLocation = () => {
  try {
    sessionStorage.removeItem('lastLocation');
  } catch (error) {
    console.warn('Error clearing last location:', error);
  }
};

/**
 * Hook para obtener información de la ruta actual
 */
export const useRouteInfo = () => {
  const location = useLocation();
  
  return {
    pathname: location.pathname,
    search: location.search,
    hash: location.hash,
    state: location.state,
    isRoot: location.pathname === '/',
    isLogin: location.pathname === '/login',
    isAdmin: location.pathname.startsWith('/admin'),
    isPublic: ['/login', '/register', '/recover', '/demo'].some(path => 
      location.pathname.startsWith(path)
    )
  };
};

/**
 * Genera breadcrumbs basados en el pathname
 */
export const generateBreadcrumbs = (pathname, routes = []) => {
  const pathSegments = pathname.split('/').filter(Boolean);
  const breadcrumbs = [];
  
  let currentPath = '';
  pathSegments.forEach(segment => {
    currentPath += `/${segment}`;
    const route = routes.find(r => r.path === currentPath);
    
    if (route) {
      breadcrumbs.push({
        path: currentPath,
        label: route.breadcrumb || segment
      });
    } else {
      breadcrumbs.push({
        path: currentPath,
        label: segment.charAt(0).toUpperCase() + segment.slice(1)
      });
    }
  });
  
  return breadcrumbs;
};

/**
 * Verifica si una ruta es accesible para el usuario actual
 */
export const isRouteAccessible = (route, userRole, userStatus) => {
  // Si la ruta no tiene restricciones, es accesible
  if (!route.adminOnly && !route.menu_producer === false) {
    return true;
  }
  
  // Si requiere ser admin y el usuario no es admin
  if (route.adminOnly && userRole !== 'admin') {
    return false;
  }
  
  // Si el usuario necesita seleccionar productor y la ruta lo requiere
  if (userStatus?.requireProducerSelection && route.menu_producer === false) {
    return false;
  }
  
  return true;
};

// Helper para withRouter - React Router v6 no tiene withRouter nativo
export const withRouter = (Component) => {
  const Wrapper = (props) => {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    
    return (
      <Component
        {...props}
        navigate={navigate}
        location={location}
        params={params}
      />
    );
  };
  
  return Wrapper;
};

// Helper para useHistory compatible con React Router v6
export const useHistory = () => {
  const navigate = useNavigate();
  return {
    push: (path) => navigate(path),
    replace: (path) => navigate(path, { replace: true }),
    goBack: () => navigate(-1),
    goForward: () => navigate(1),
    listen: () => () => {}, // No-op para compatibilidad
    block: () => () => {}, // No-op para compatibilidad
    location: window.location,
    createHref: (to) => to
  };
};

// Exportar Navigate para uso directo
export { Navigate };
